/**
 * Recently Viewed Products Module
 * Handles tracking and displaying recently viewed products
 */

class RecentlyViewed {
    constructor() {
      this.storageKey = 'recently_viewed';
      this.maxProducts = this.getMaxProducts();
      this.init();
    }
  
    /**
     * Get maximum number of products from data attribute
     */
    getMaxProducts() {
      const element = document.querySelector('recently-viewed');
      return parseInt(element?.getAttribute("data-products-count") || "8", 10);
    }
  
    /**
     * Escape quotes in strings to prevent JavaScript injection
     */
    escapeQuotes(str) {
      if (typeof str !== 'string') return str;
      return str.replace(/'/g, "\\'").replace(/"/g, '\\"');
    }
  
    /**
     * Get existing recently viewed data from sessionStorage
     */
    getStoredData() {
      try {
        const data = sessionStorage.getItem(this.storageKey);
        return data ? JSON.parse(data) : [];
      } catch (error) {
        return [];
      }
    }
  
    /**
     * Save data to sessionStorage
     */
    saveData(data) {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(data));
      } catch (error) {
        // Silently handle storage errors
      }
    }
  
    /**
     * Add current product to recently viewed list
     */
    addCurrentProduct() {
      const currentPdpData = window.pdpData || {};

      if (!currentPdpData?.productTitle?.trim?.()) {
        return;
      }
  
      const currentProduct = {
        ...currentPdpData,
        productTitle: this.escapeQuotes(currentPdpData.productTitle.trim())
      };
      const existingData = this.getStoredData();
      const currentTitle = currentProduct.productTitle;
  
      // Check if product is already in the list
      const isDuplicate = existingData.some(item => item.productTitle === currentTitle);
      
      if (isDuplicate) {
        return;
      }
  
      // Add new product to the beginning
      const updatedData = [currentProduct, ...existingData];
  
      // Limit to max products
      if (updatedData.length > this.maxProducts) {
        updatedData.splice(this.maxProducts);
      }
  
    this.saveData(updatedData);
  }

  /**
   * Generate HTML for recently viewed products
   */
  generateProductHTML(product) {
    const escapedTitle = this.escapeQuotes(product.productTitle || '');
    return `
      <div class="recently-viewed-grid-item">
        <product-card class="block w-full h-full">
          <a class="contents" href="${product.productUrl}" title="${escapedTitle}" aria-label="${escapedTitle}">
            <div class="product-media-container media-fit product-media-container--image w-full h-full">
              <div class="product-media w-full h-full p-2">
                ${product.productImg}
              </div>
            </div>
          </a>
        </product-card>
      </div>
    `;
  }

  /**
   * Render recently viewed products to the page
   */
  renderProducts() {
    const products = this.getStoredData();
    const recentlyViewedElement = document.querySelector('recently-viewed');
    const blocks = document.querySelectorAll('recently-viewed [data-slider-block], recently-viewed .slider-content');
    const sectionElement = recentlyViewedElement?.closest('section');
    
    if (!products.length) {
      if (sectionElement) {
        sectionElement.classList.add('hidden');
      }
      return;
    }

    const limitedProducts = products.slice(0, this.maxProducts);
    
    // Set data-has-recently-viewed to true on the recently-viewed element
    if (recentlyViewedElement) {
      recentlyViewedElement.setAttribute('data-has-recently-viewed', 'true');
    }

    if (sectionElement) {
      sectionElement.classList.remove('hidden');
    }
    
    // Find all recently-viewed blocks and inject products
    blocks.forEach(block => {
      const contentEl = block.querySelector('.slider-content') || block;
      this.renderContainer(contentEl, limitedProducts);
    });

    // Initialize sliders after products are injected
    this.initializeSliders();
  }

  /**
   * Render products in container
   */
  renderContainer(element, products) {
    const html = products
      .map(product => this.generateProductHTML(product))
      .join('');
    
    element.innerHTML = html;
  }

  /**
   * Initialize sliders after products are rendered
   */
  initializeSliders() {
    // Find all recently-viewed slider blocks
    const sliderBlocks = document.querySelectorAll('recently-viewed [data-slider-block]');
    
    if (sliderBlocks.length === 0) return;

    // Wait for Swiper to be available
    const initSlider = () => {
      if (typeof window.Swiper === 'undefined') {
        setTimeout(initSlider, 100);
        return;
      }

      sliderBlocks.forEach(block => {
        this.initializeSingleSlider(block);
      });
    };

    initSlider();
  }

  /**
   * Initialize a single slider block
   */
  initializeSingleSlider(element) {
    const contentEl = element.querySelector('.slider-content');
    if (!contentEl) return;

    // Get direct children (product divs)
    const slides = [...contentEl.children].filter(el => 
      !['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'].includes(el.tagName)
    );
    
    if (slides.length < 1) return;

    // Create Swiper wrapper
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper', 'w-full', 'max-w-full', 'overflow-x-hidden');
    
    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move slides into wrapper
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    contentEl.innerHTML = '';
    contentEl.appendChild(swiperEl);

    // Get Swiper options from data attribute
    const customOptions = element.dataset.swiperOptions 
      ? JSON.parse(element.dataset.swiperOptions) 
      : {};

    // Default options
    const defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 16,
    };

    // Enable loop if more than 1 slide
    if (slides.length > 1 && customOptions.loop !== false) {
      defaultOptions.loop = true;
    }

    // Add navigation if controls exist
    const blockId = element.dataset.blockId;
    const controlsElement = element.querySelector('[data-slider-control]');
    
    if (controlsElement && blockId) {
      const nextBtn = controlsElement.querySelector('.slider-navigation-next');
      const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
      
      if (nextBtn && prevBtn) {
        customOptions.navigation = {
          nextEl: nextBtn,
          prevEl: prevBtn,
        };
      }
    }

    // Initialize Swiper
    new window.Swiper(swiperEl, {
      ...defaultOptions,
      ...customOptions,
    });
  }

  /**
   * Initialize the recently viewed functionality
   */
  init() {
    // Add current product to recently viewed
    this.addCurrentProduct();

    // Render on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.renderProducts());
    } else {
      this.renderProducts();
    }
  }
}
  
  // Initialize the module
  new RecentlyViewed();